import React, { useState } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Seo from '../../components/Seo/Seo';
import services from '../../utils/services';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const categories = [
    'All',
    'Before & After',
  ];

  const [activeCategory, setActiveCategory] = useState('All');

  const beforeAfterItems = [
    {
      id: 101,
      serviceSlug: 'carpet-cleaning-brisbane',
      category: 'Before & After',
      title: 'Carpet Transformation',
      description: 'Before and after comparison of professional carpet steam cleaning.',
      beforeImage: '/BeforeAfter/CarpetBefore.png',
      afterImage: '/BeforeAfter/CarpetAfter.png',
    },
    {
      id: 102,
      serviceSlug: 'couch-cleaning-brisbane',
      category: 'Before & After',
      title: 'Couch Deep Clean',
      description: 'Upholstery cleaning result with visible stain and odour removal.',
      beforeImage: '/BeforeAfter/CouchBefore.png',
      afterImage: '/BeforeAfter/CouchAfter.png',
    },
    {
      id: 103,
      serviceSlug: 'mattress-cleaning-brisbane',
      category: 'Before & After',
      title: 'Mattress Sanitisation',
      description: 'Deep mattress cleaning and sanitisation before/after result.',
      beforeImage: '/BeforeAfter/MattressBefore.png',
      afterImage: '/BeforeAfter/MattressAfter.png',
    },
    {
      id: 104,
      serviceSlug: 'tile-and-grout-cleaning-brisbane',
      category: 'Before & After',
      title: 'Tile and Grout Refresh',
      description: 'Tile and grout deep-cleaning comparison before and after treatment.',
      beforeImage: '/BeforeAfter/TileBefore.png',
      afterImage: '/BeforeAfter/TileAfter.png',
    },
    {
      id: 105,
      serviceSlug: 'end-of-lease-cleaning-brisbane',
      category: 'Before & After',
      title: 'End of Lease Cleaning(Bond Cleaning)',
      description: 'Stunning before and after result of a full bond cleaning service.',
      beforeImage: '/BeforeAfter/BondBefore.png',
      afterImage: '/BeforeAfter/BondAfter.png',
    },
  ];

  const validBeforeAfterItems = beforeAfterItems.filter((item) => {
    const relatedService = services.find((service) => service.slug === item.serviceSlug);
    return Boolean(relatedService?.image && item.beforeImage && item.afterImage);
  });

  const galleryItems = [...validBeforeAfterItems];

  const filteredItems =
    activeCategory === 'All'
      ? galleryItems
      : galleryItems.filter((item) => item.category === activeCategory);

  const openLightbox = (item) => {
    setSelectedImage(item);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  };

  const navigateImage = (direction) => {
    const currentIndex = filteredItems.findIndex((item) => item.id === selectedImage.id);
    let newIndex;

    if (direction === 'next') {
      newIndex = currentIndex + 1 >= filteredItems.length ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex - 1 < 0 ? filteredItems.length - 1 : currentIndex - 1;
    }

    setSelectedImage(filteredItems[newIndex]);
  };

  return (
    <div>
      <Seo
        title="Gallery"
        description="Browse before-and-after gallery images from The Skilled Group's carpet, couch, mattress, and tile cleaning work."
        canonicalPath="/gallery"
      />
      {/* Page Header */}
      <div className="page-header">
        <div className="container-custom">
          <h1>Our Gallery</h1>
          <p>
            See the quality of our work. Browse through our portfolio of cleaning
            projects across Brisbane.
          </p>
        </div>
      </div>

      {/* Gallery Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  activeCategory === category
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                onClick={() => openLightbox(item)}
                className="group cursor-pointer rounded-xl overflow-hidden border border-gray-100 bg-white hover:shadow-lg transition-all duration-300 flex flex-col h-full"
              >
                {item.beforeImage && item.afterImage ? (
                  <div className="h-40 sm:h-48 lg:h-56 relative overflow-hidden flex-none">
                    <div className="grid h-full grid-cols-2">
                      <div className="relative">
                        <img
                          src={item.beforeImage}
                          alt={`${item.title} before`}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                        <span className="absolute left-2 top-2 rounded bg-black/65 px-2 py-1 text-[10px] font-semibold tracking-wide text-white">
                          BEFORE
                        </span>
                      </div>
                      <div className="relative">
                        <img
                          src={item.afterImage}
                          alt={`${item.title} after`}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                        <span className="absolute right-2 top-2 rounded bg-success px-2 py-1 text-[10px] font-semibold tracking-wide text-white">
                          AFTER
                        </span>
                      </div>
                    </div>

                    <div className="absolute inset-0 bg-primary-500 bg-opacity-0 group-hover:bg-opacity-35 transition-all duration-300 flex items-center justify-center">
                      <span className="text-white font-semibold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        View Details
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`${item.color} h-56 flex items-center justify-center relative overflow-hidden`}
                  >
                    <div className="text-center p-4">
                      <p className="text-gray-600 font-heading font-semibold text-sm">
                        {item.title}
                      </p>
                      <p className="text-gray-400 text-xs mt-1">{item.category}</p>
                    </div>

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-primary-500 bg-opacity-0 group-hover:bg-opacity-70 transition-all duration-300 flex items-center justify-center">
                      <span className="text-white font-semibold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        View Details
                      </span>
                    </div>
                  </div>
                )}

                {/* Info */}
                <div className="p-4 border-t border-gray-100 bg-white relative z-10">
                  <h3 className="font-heading font-semibold text-gray-800 text-sm mb-1 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-1">
                    {item.description}
                  </p>
                  <p className="text-gray-400 text-xs">{item.category}</p>
                </div>
              </div>
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-400 text-lg">No images found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-white/95 hover:bg-white shadow-sm transition-colors text-gray-600"
              aria-label="Close"
            >
              <FaTimes />
            </button>

            {/* Image area */}
            <div className={`${selectedImage.color || 'bg-gray-100'} h-[48vh] min-h-[260px] md:h-96 flex items-center justify-center relative`}>
              {selectedImage.beforeImage && selectedImage.afterImage ? (
                <div className="grid h-full w-full grid-cols-2">
                  <div className="relative">
                    <img
                      src={selectedImage.beforeImage}
                      alt={`${selectedImage.title} before`}
                      className="h-full w-full object-cover"
                    />
                    <span className="absolute left-3 top-3 rounded bg-black/65 px-2 py-1 text-xs font-semibold tracking-wide text-white">
                      BEFORE
                    </span>
                  </div>
                  <div className="relative">
                    <img
                      src={selectedImage.afterImage}
                      alt={`${selectedImage.title} after`}
                      className="h-full w-full object-cover"
                    />
                    <span className="absolute right-3 top-3 rounded bg-success px-2 py-1 text-xs font-semibold tracking-wide text-white">
                      AFTER
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center p-6">
                  <p className="text-gray-700 font-heading font-bold text-xl">
                    {selectedImage.title}
                  </p>
                  <p className="text-gray-500 text-sm mt-2">{selectedImage.category}</p>
                </div>
              )}

              {/* Navigation arrows */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage('prev');
                }}
                className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 bg-white bg-opacity-80 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all"
                aria-label="Previous image"
              >
                <FaChevronLeft className="text-gray-700" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateImage('next');
                }}
                className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 bg-white bg-opacity-80 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all"
                aria-label="Next image"
              >
                <FaChevronRight className="text-gray-700" />
              </button>
            </div>

            {/* Info */}
            <div className="p-5 sm:p-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h3 className="text-xl font-heading font-bold text-gray-800 mb-1">
                  {selectedImage.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">{selectedImage.description}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;